import React, { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useBrand, splitBrandName } from '@/hooks/useBrand'
import { useFranchise } from '@/hooks/useFranchise'
import { useImpersonation } from '@/hooks/useImpersonation'
import {
  LayoutDashboard,
  MessageSquare,
  BarChart3,
  Users,
  Code,
  Settings,
  ChevronLeft,
  ChevronDown,
  Phone,
  Users2,
  Plug,
  Shield,
  Bot,
  Calendar,
  Mail,
  Megaphone,
  PhoneOff,
  PhoneForwarded,
  Sparkles,
  CreditCard,
  KeyRound,
  ArrowLeft,
  Palette,
  Crown,
  Building2
} from 'lucide-react'
import { cn } from '@/lib/utils'
import api from '@/lib/api'
import { useAffiliatePreview } from '@/hooks/useAffiliatePreview'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import Wordmark from './Wordmark'
import { useHelpChat } from '@/components/help/HelpChatContext'
import { TierBadge } from '@/components/ui/TierBadge'
import { useFeatureGate, Feature } from '@/hooks/useFeatureGate'
import { Lock } from 'lucide-react'

interface NavItem {
  name: string
  href: string
  icon: React.ElementType
  badge?: number
  roles?: ('admin' | 'supervisor' | 'agent')[]
  platformAdmin?: boolean
  children?: NavItem[]
  onClick?: () => void
  highlight?: boolean
  nudge?: boolean
  /** If set, this nav item is gated — show a lock icon when the feature isn't
      included in the org's tier. Clicking still navigates (soft gating); the
      destination page will surface the upgrade prompt. */
  gatedBy?: Feature
}

interface NavSection {
  label: string
  items: NavItem[]
  roles?: ('admin' | 'supervisor' | 'agent')[]
  collapsible?: boolean
}

// Define navigation sections
const getNavigationSections = (handlers: { onAskHelp: () => void; askHelpNudge: boolean; isAffiliate: boolean; isFranchiseAdmin: boolean; affiliateLinkQuery: string; affiliateEmailUnread: number; affiliateDealAccess: string; appBaseName: string }): NavSection[] => [
  {
    label: 'MAIN',
    items: [
      { name: 'Dashboard', href: '/', icon: LayoutDashboard }
    ]
  },
  {
    label: 'ACTIVITY',
    items: [
      { name: 'Calls', href: '/calls', icon: Phone, gatedBy: 'ai_inbound_agent' },
      { name: 'Live Chats', href: '/chats', icon: MessageSquare },
      { name: 'Follow-Up Queue', href: '/follow-up-queue', icon: PhoneForwarded, gatedBy: 'auto_callback' },
    ]
  },
  {
    label: 'MANAGEMENT',
    items: [
      { name: 'Leads', href: '/leads', icon: Users },
      { name: 'Bookings Calendar', href: '/bookings', icon: Calendar },
      { name: 'Team Messages', href: '/messages', icon: Mail },
    ]
  },
  {
    label: 'AI AGENTS',
    items: [
      { name: 'AI Agents', href: '/agents', icon: Bot, gatedBy: 'ai_inbound_agent' },
    ]
  },
  {
    label: 'INSIGHTS',
    items: [
      { name: 'Analytics', href: '/analytics', icon: BarChart3, roles: ['admin', 'supervisor'] }
    ],
    roles: ['admin', 'supervisor']
  },
  {
    label: 'ADMIN',
    collapsible: true,
    roles: ['admin'],
    items: [
      { name: 'Team & Users', href: '/team', icon: Users2, roles: ['admin'] },
      { name: 'Chat Widget', href: '/widget', icon: Code, roles: ['admin'] },
      { name: 'Integrations', href: '/integrations', icon: Plug, roles: ['admin'], gatedBy: 'crm_integrations' },
    ]
  },
  {
    label: 'HELP',
    items: [
      {
        name: `Ask ${handlers.appBaseName}`,
        href: '#help',
        icon: Sparkles,
        onClick: handlers.onAskHelp,
        highlight: true,
        nudge: handlers.askHelpNudge,
      }
    ]
  },
  ...(handlers.isFranchiseAdmin
    ? [{
        // The franchise owner's version of what root used to call Platform
        // Admin — scoped to their subtree. Root's own console is The Castle.
        label: 'PLATFORM ADMIN',
        items: [
          { name: 'Clients', href: '/franchise/clients', icon: Building2 },
          { name: 'Branding', href: '/franchise/branding', icon: Palette },
        ]
      }]
    : []),
  {
    label: '',
    items: [
      ...(handlers.isAffiliate
        ? [
            { name: 'Affiliate', href: `/affiliate${handlers.affiliateLinkQuery}`, icon: Users2 },
            {
              name: 'Affiliate Email',
              href: `/affiliate/email${handlers.affiliateLinkQuery}`,
              icon: Mail,
              badge: handlers.affiliateEmailUnread > 0 ? handlers.affiliateEmailUnread : undefined,
            },
            ...(handlers.affiliateDealAccess && handlers.affiliateDealAccess !== 'none'
              ? [{ name: 'Deal Builder', href: '/affiliate/deals', icon: CreditCard }]
              : []),
          ]
        : []),
      { name: 'Billing', href: '/settings/billing', icon: CreditCard, roles: ['admin'] },
      { name: 'Settings', href: '/settings', icon: Settings }
    ]
  }
]

const platformAdminNavItem: NavItem = {
  name: 'The Castle',
  href: '/admin',
  icon: Shield,
  platformAdmin: true,
  children: [
    { name: 'Dashboard', href: '/admin', icon: LayoutDashboard, platformAdmin: true },
    { name: 'Franchises', href: '/admin/franchises', icon: Crown, platformAdmin: true },
    { name: 'Organizations', href: '/admin/organizations', icon: Users, platformAdmin: true },
    { name: 'All Users', href: '/admin/users', icon: Users2, platformAdmin: true },
    { name: 'Voice & Costs', href: '/admin/voice', icon: Phone, platformAdmin: true },
    { name: 'Custom LLMs', href: '/admin/custom-llm-providers', icon: Bot, platformAdmin: true },
    { name: 'Access Codes', href: '/admin/access-codes', icon: KeyRound, platformAdmin: true },
    { name: 'Affiliates', href: '/admin/affiliates', icon: Users2, platformAdmin: true },
    { name: 'Deal Builder', href: '/admin/deals', icon: CreditCard, platformAdmin: true },
    { name: 'Demo Leads', href: '/admin/demo-leads', icon: Megaphone, platformAdmin: true },
    { name: 'Demo DNC', href: '/admin/demo-dnc', icon: PhoneOff, platformAdmin: true }
  ]
}

interface AppSidebarProps {
  collapsed: boolean
  onToggle: () => void
  isMobile?: boolean
  mobileOpen?: boolean
  onMobileClose?: () => void
  badges?: Record<string, number>
}

export default function AppSidebar({
  collapsed,
  onToggle,
  isMobile = false,
  mobileOpen = false,
  onMobileClose,
  badges = {}
}: AppSidebarProps) {
  const location = useLocation()
  const { agent } = useAuth()
  const brand = useBrand()
  const { isImpersonating, impersonatedOrg } = useImpersonation()
  const { isPreview: isAffiliatePreview, linkQuery: affiliateLinkQuery } = useAffiliatePreview()
  const { isFranchiseAdmin } = useFranchise()
  const { openHelp, showNudge } = useHelpChat()
  const featureGate = useFeatureGate()
  const [adminOpen, setAdminOpen] = useState(true)
  const [configOpen, setConfigOpen] = useState(location.pathname.startsWith('/admin'))
  const [bookingReminderFlash, setBookingReminderFlash] = useState(false)
  const [humanRequestFlash, setHumanRequestFlash] = useState(false)
  const [isAffiliate, setIsAffiliate] = useState(false)
  const [affiliateDealAccess, setAffiliateDealAccess] = useState('none')
  const [affiliateEmailUnread, setAffiliateEmailUnread] = useState(0)

  // Editable section labels
  const EDITABLE_SECTIONS = ['ACTIVITY', 'MANAGEMENT', 'AI AGENTS', 'INSIGHTS']
  const [customLabels, setCustomLabels] = useState<Record<string, string>>(() => {
    try {
      return JSON.parse(localStorage.getItem('app_sidebar_labels') || '{}')
    } catch { return {} }
  })
  const [editingLabel, setEditingLabel] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const labelInputRef = useRef<HTMLInputElement>(null)

  const getSectionLabel = (defaultLabel: string) => customLabels[defaultLabel] || defaultLabel

  const startEditingLabel = (defaultLabel: string) => {
    setEditingLabel(defaultLabel)
    setEditValue(getSectionLabel(defaultLabel))
    setTimeout(() => labelInputRef.current?.select(), 0)
  }

  const saveLabel = () => {
    if (!editingLabel) return
    const trimmed = editValue.trim().toUpperCase()
    const updated = { ...customLabels }
    if (!trimmed || trimmed === editingLabel) {
      delete updated[editingLabel]
    } else {
      updated[editingLabel] = trimmed
    }
    setCustomLabels(updated)
    localStorage.setItem('app_sidebar_labels', JSON.stringify(updated))
    setEditingLabel(null)
  }

  // Listen for booking reminder events to flash the Bookings nav item
  useEffect(() => {
    const handler = () => {
      setBookingReminderFlash(true)
      setTimeout(() => setBookingReminderFlash(false), 30_000)
    }
    window.addEventListener('booking-reminder', handler)
    return () => window.removeEventListener('booking-reminder', handler)
  }, [])

  // Listen for human request events to flash the Chats nav item
  useEffect(() => {
    const handler = () => {
      setHumanRequestFlash(true)
      setTimeout(() => setHumanRequestFlash(false), 30_000)
    }
    window.addEventListener('human-requested', handler)
    return () => window.removeEventListener('human-requested', handler)
  }, [])

  const isPlatformAdmin = (agent as any)?.isPlatformAdmin
  const isSuperAdmin = (agent as any)?.isSuperAdmin
  const userRole = agent?.role as 'admin' | 'supervisor' | 'agent' | undefined
  const onAdminRoute = location.pathname.startsWith('/admin')

  useEffect(() => {
    let cancelled = false
    const loadAffiliate = async () => {
      if (!agent?.id) {
        setIsAffiliate(false)
        setAffiliateDealAccess('none')
        return
      }
      try {
        const res = await api.get('/api/affiliates/me')
        if (!cancelled) {
          setIsAffiliate(true)
          setAffiliateDealAccess(res.data?.affiliate?.dealBuilderAccess || 'none')
        }
      } catch {
        if (!cancelled) {
          setIsAffiliate(false)
          setAffiliateDealAccess('none')
        }
      }
    }
    loadAffiliate()
    return () => { cancelled = true }
  }, [agent?.id])

  // Poll the affiliate email unread count for the sidebar badge. Only runs
  // for affiliates (or platform admins previewing one) — non-affiliates
  // would just get a quiet 0 back, but no point burning the request. The
  // panel also dispatches an event after every load so opening a thread
  // updates the badge instantly without waiting for the next poll tick.
  useEffect(() => {
    const enabled = isAffiliate || (isPlatformAdmin && isAffiliatePreview)
    if (!enabled) {
      setAffiliateEmailUnread(0)
      return
    }
    let cancelled = false
    const fetchCount = async () => {
      try {
        const params = isAffiliatePreview && affiliateLinkQuery
          ? { as: affiliateLinkQuery.replace(/^\?as=/, '') }
          : undefined
        const res = await api.get('/api/affiliates/me/email/unread-count', { params })
        if (!cancelled) setAffiliateEmailUnread(res.data?.count || 0)
      } catch {
        if (!cancelled) setAffiliateEmailUnread(0)
      }
    }
    fetchCount()
    const interval = window.setInterval(fetchCount, 60_000)
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ count: number }>).detail
      if (typeof detail?.count === 'number') setAffiliateEmailUnread(detail.count)
    }
    // Inbound-email push from useAlertNotifications — refetch right away so
    // the badge bumps without the user waiting up to 60s for the next poll.
    const refetch = () => { void fetchCount() }
    window.addEventListener('affiliate-email-unread', handler)
    window.addEventListener('affiliate-email-received', refetch)
    return () => {
      cancelled = true
      window.clearInterval(interval)
      window.removeEventListener('affiliate-email-unread', handler)
      window.removeEventListener('affiliate-email-received', refetch)
    }
  }, [isAffiliate, isPlatformAdmin, isAffiliatePreview, affiliateLinkQuery])

  // Auto-expand Platform Admin when navigating to admin routes
  useEffect(() => {
    if (onAdminRoute) setConfigOpen(true)
  }, [onAdminRoute])

  // Check if user can see a nav item
  const canSeeItem = (item: NavItem) => {
    if (item.platformAdmin) return onAdminRoute || isPlatformAdmin
    // On /admin routes, org-level items are hidden (platform nav takes over)
    if (onAdminRoute) return false
    if (!item.roles) return true
    return userRole && item.roles.includes(userRole)
  }

  // Check if user can see a section
  const canSeeSection = (section: NavSection) => {
    // On /admin routes, org-level sections are hidden (platform nav takes over)
    if (onAdminRoute) return false
    if (!section.roles) return true
    return userRole && section.roles.includes(userRole)
  }

  // Close mobile sidebar when navigating
  const handleNavClick = () => {
    if (isMobile && onMobileClose) {
      onMobileClose()
    }
  }

  // Check if a path is active
  const isActivePath = (href: string) => {
    // Strip query — nav items carry ?as=<id> in preview mode but we still
    // want them to match the current pathname.
    const path = href.split('?')[0]
    if (path === '/') return location.pathname === '/'
    if (path === '/admin') return location.pathname === '/admin'
    // Settings has a sibling nav item at /settings/billing — require exact match
    // so landing on /settings/billing doesn't light up Settings too.
    if (path === '/settings') return location.pathname === '/settings'
    // Same disambiguation for affiliate: /affiliate has a sibling /affiliate/email
    // so the dashboard nav item shouldn't light up while on email.
    if (path === '/affiliate') return location.pathname === '/affiliate'
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  // Render a navigation item
  const renderNavItem = (item: NavItem, isChild = false) => {
    if (!canSeeItem(item)) return null

    const Icon = item.icon
    const isActive = isActivePath(item.href)
    const hasChildren = item.children && item.children.length > 0

    // Item with children (Configuration submenu)
    if (hasChildren) {
      const isChildActive = item.children?.some(child => isActivePath(child.href))

      if (collapsed && !isMobile) {
        return (
          <Tooltip key={item.name}>
            <TooltipTrigger asChild>
              <div
                className={cn(
                  'flex items-center justify-center rounded-lg px-3 py-2.5 text-sm font-medium transition-colors cursor-pointer',
                  'hover:bg-accent hover:text-accent-foreground',
                  isChildActive
                    ? 'bg-gradient-brand text-white'
                    : 'text-muted-foreground'
                )}
              >
                <Icon className={cn('h-5 w-5 flex-shrink-0', isChildActive && 'text-white')} />
              </div>
            </TooltipTrigger>
            <TooltipContent side="right" className="flex flex-col gap-1">
              {item.name}
              <div className="border-t border-border mt-1 pt-1 space-y-1">
                {item.children?.filter(canSeeItem).map(child => (
                  <Link
                    key={child.name}
                    to={child.href}
                    onClick={handleNavClick}
                    className="block text-sm hover:text-foreground"
                  >
                    {child.name}
                  </Link>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        )
      }

      return (
        <Collapsible key={item.name} open={configOpen} onOpenChange={setConfigOpen}>
          <CollapsibleTrigger asChild>
            <button
              className={cn(
                'w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                'hover:bg-accent hover:text-accent-foreground',
                isChildActive
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground'
              )}
            >
              <Icon className="h-5 w-5 flex-shrink-0" />
              <span className="flex-1 text-left">{item.name}</span>
              <ChevronDown className={cn(
                'h-4 w-4 transition-transform duration-200',
                configOpen && 'rotate-180'
              )} />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="pl-4 mt-1 space-y-1">
            {item.children?.filter(canSeeItem).map(child => renderNavItem(child, true))}
          </CollapsibleContent>
        </Collapsible>
      )
    }

    // Regular nav item — renders as <button> if onClick handler is present, otherwise <Link>
    const sharedClassName = cn(
      'relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-200',
      'hover:bg-accent/80 hover:text-accent-foreground hover:translate-x-0.5',
      isActive
        ? 'bg-gradient-brand text-white shadow-md shadow-brand-royal/25 hover:bg-gradient-brand-dark hover:translate-x-0'
        : item.highlight
          ? 'text-foreground bg-accent/40 border border-brand-royal/20 hover:bg-accent'
          : 'text-muted-foreground',
      !isActive && item.nudge && 'ring-2 ring-brand-royal/40 ring-offset-2 ring-offset-background shadow-lg shadow-brand-royal/20',
      (collapsed && !isMobile) && 'justify-center',
      isChild && 'py-2',
      item.href === '/bookings' && bookingReminderFlash && !isActive && 'animate-pulse bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
      item.href === '/chats' && humanRequestFlash && !isActive && 'animate-pulse bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
    )
    const iconClass = cn(
      'flex-shrink-0',
      isActive && 'text-white',
      !isActive && item.highlight && 'text-brand-royal',
      !isActive && item.nudge && 'animate-pulse',
      isChild ? 'h-4 w-4' : 'h-5 w-5'
    )
    const isLocked = Boolean(item.gatedBy) && !featureGate.isAllowed(item.gatedBy!)
    const labelContent = (!collapsed || isMobile) && (
      <>
        <span className={cn('flex-1 text-left', isLocked && !isActive && 'text-muted-foreground/70')}>
          {item.name}
        </span>
        {isLocked && !isActive && (
          <Lock className="h-3 w-3 text-muted-foreground/60 ml-1" />
        )}
        {item.nudge && !isActive && (
          <span className="ml-1 inline-flex items-center rounded-full bg-gradient-to-r from-brand-royal to-brand-indigo px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white shadow-sm shadow-brand-royal/40">
            New
          </span>
        )}
        {(item.badge || badges[item.href]) ? (
          <span className="ml-auto flex h-5 min-w-5 px-1 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
            {item.badge || badges[item.href]}
          </span>
        ) : null}
      </>
    )

    const navButton = item.onClick ? (
      <button
        key={item.name}
        type="button"
        onClick={() => {
          item.onClick?.()
        }}
        className={cn(sharedClassName, 'w-full')}
      >
        <Icon className={iconClass} />
        {labelContent}
      </button>
    ) : (
      <Link
        key={item.name}
        to={item.href}
        onClick={() => {
          handleNavClick()
          if (item.href === '/bookings') setBookingReminderFlash(false)
        }}
        className={sharedClassName}
      >
        <Icon className={iconClass} />
        {labelContent}
      </Link>
    )

    if (collapsed && !isMobile && !isChild) {
      return (
        <Tooltip key={item.name}>
          <TooltipTrigger asChild>{navButton}</TooltipTrigger>
          <TooltipContent side="right" className="flex items-center gap-2">
            {item.name}
            {(item.badge || badges[item.href]) ? (
              <span className="ml-2 flex h-5 min-w-5 px-1 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                {item.badge || badges[item.href]}
              </span>
            ) : null}
          </TooltipContent>
        </Tooltip>
      )
    }

    return navButton
  }

  // Render a section
  const renderSection = (section: NavSection) => {
    if (!canSeeSection(section)) return null

    const visibleItems = section.items.filter(canSeeItem)
    if (visibleItems.length === 0) return null

    const sectionContent = (
      <div className="space-y-1">
        {visibleItems.map(item => renderNavItem(item))}
      </div>
    )

    // Collapsible section (ADMIN)
    if (section.collapsible) {
      if (collapsed && !isMobile) {
        return (
          <div key={section.label} className="mt-2">
            {sectionContent}
          </div>
        )
      }

      return (
        <div key={section.label} className="mt-3">
          <Collapsible open={adminOpen} onOpenChange={setAdminOpen}>
            <CollapsibleTrigger asChild>
              <button className="w-full flex items-center justify-between px-3 py-1 text-[10px] font-bold text-muted-foreground/80 uppercase tracking-[0.14em] hover:text-foreground transition-colors">
                <span>{section.label}</span>
                <ChevronDown className={cn(
                  'h-3 w-3 transition-transform duration-200',
                  adminOpen && 'rotate-180'
                )} />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-1">
              {sectionContent}
            </CollapsibleContent>
          </Collapsible>
        </div>
      )
    }

    // Regular section
    const isEditable = EDITABLE_SECTIONS.includes(section.label)
    const displayLabel = getSectionLabel(section.label)

    return (
      <div key={section.label || 'settings'} className="mt-3 first:mt-0">
        {(!collapsed || isMobile) && section.label && (
          editingLabel === section.label ? (
            <input
              ref={labelInputRef}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={saveLabel}
              onKeyDown={(e) => { if (e.key === 'Enter') saveLabel(); if (e.key === 'Escape') setEditingLabel(null) }}
              className="px-3 py-0.5 text-xs font-bold uppercase tracking-wider bg-accent/50 rounded outline-none ring-1 ring-brand-royal/30 w-full"
              maxLength={20}
              autoFocus
            />
          ) : (
            <span
              className={cn(
                'px-3 py-1 text-[10px] font-bold text-muted-foreground/80 uppercase tracking-[0.14em]',
                isEditable && 'cursor-pointer hover:text-foreground transition-colors'
              )}
              onDoubleClick={() => isEditable && startEditingLabel(section.label)}
              title={isEditable ? 'Double-click to rename' : undefined}
            >
              {displayLabel}
            </span>
          )
        )}
        <div className="mt-1">
          {sectionContent}
        </div>
      </div>
    )
  }

  const navigationSections = getNavigationSections({
    onAskHelp: () => {
      if (isMobile && onMobileClose) onMobileClose()
      openHelp()
    },
    askHelpNudge: showNudge,
    // Platform admins previewing an affiliate (?as=<id>) see the same nav
    // items as the affiliate themselves, so they can move between the
    // dashboard and email surfaces while previewing.
    isAffiliate: isAffiliate || (isPlatformAdmin && isAffiliatePreview),
    isFranchiseAdmin,
    affiliateLinkQuery,
    affiliateEmailUnread,
    affiliateDealAccess,
    appBaseName: splitBrandName(brand.appName)[0],
  })

  return (
    <TooltipProvider delayDuration={0}>
      {/* Mobile overlay — blocks body scroll and provides tap-to-close */}
      {isMobile && mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 backdrop-blur-sm"
          onClick={onMobileClose}
        />
      )}
      <aside
        className={cn(
          'fixed left-0 z-40 border-r border-border/50 bg-card/95 backdrop-blur-xl transition-colors duration-200 flex flex-col',
          isImpersonating ? 'top-10 h-[calc(100vh-2.5rem)]' : 'top-0 h-screen',
          isMobile
            ? cn(
                'w-64 transform',
                mobileOpen ? 'translate-x-0' : '-translate-x-full'
              )
            : collapsed ? 'w-16' : 'w-64'
        )}
      >
        {/* Logo & Toggle */}
        <div className="relative flex h-16 items-center justify-between px-4 overflow-hidden">
          {/* Subtle brand glow behind the wordmark */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-8 -left-4 h-32 w-48 rounded-full opacity-60 blur-2xl"
            style={{ background: 'radial-gradient(circle, hsl(var(--grad-periwinkle) / 0.15) 0%, transparent 70%)' }}
          />
          <Link to="/" className="relative hover:opacity-80 transition-opacity press-scale">
            {!collapsed && (
              <Wordmark size="md" />
            )}
            {collapsed && (
              <Wordmark size="sm" iconOnly className="mx-auto" />
            )}
          </Link>
        </div>
        <div className="mx-3 border-b border-border/40" />

        {/* Toggle Button - hidden on mobile */}
        {!isMobile && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggle}
            className={cn(
              'absolute -right-3 top-20 z-50 h-6 w-6 rounded-full border border-border bg-background shadow-md hover:bg-accent',
              collapsed && 'rotate-180'
            )}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto overscroll-contain p-3 pb-32" style={{ WebkitOverflowScrolling: 'touch' }}>
          {/* Exit-to-org link when inside Platform Admin — prevents the
              "navigation all over the place" feeling where the only way back
              to the org dashboard was the avatar dropdown's Switch Account. */}
          {onAdminRoute && (!collapsed || isMobile) && (
            <Link
              to="/"
              onClick={handleNavClick}
              className="flex items-center gap-2 px-3 py-2 mb-2 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-accent/60 transition-colors border border-dashed border-border/60"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              <span>Exit The Castle</span>
            </Link>
          )}
          {onAdminRoute && collapsed && !isMobile && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to="/"
                  onClick={handleNavClick}
                  className="flex items-center justify-center h-9 w-9 mb-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent/60 transition-colors border border-dashed border-border/60"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right">Exit The Castle</TooltipContent>
            </Tooltip>
          )}
          {navigationSections.map(section => renderSection(section))}

          {/* Platform Admin Section — shown for platform admins */}
          {isPlatformAdmin && (
            <div className={cn(onAdminRoute ? 'mt-0' : 'mt-4 pt-4 border-t border-border')}>
              {(!collapsed || isMobile) && (
                <span className="px-3 py-1 text-xs font-bold text-foreground/70 uppercase tracking-wider">
                  Platform
                </span>
              )}
              <div className="mt-1 space-y-1">
                {renderNavItem(platformAdminNavItem)}
              </div>
            </div>
          )}
        </nav>

        {/* Footer - Agent Info */}
        {(!collapsed || isMobile) && (() => {
          // Plan line prefers the impersonated org's tier so super admins can
          // see what tier they're testing as. Falls back to the logged-in
          // agent's own subscriptionTier when not impersonating.
          const displayedTier =
            impersonatedOrg?.subscriptionTier ?? (agent as any)?.subscriptionTier
          return (
          <Link to={`/team/profile/${agent?.id}`} className="block border-t border-border/40 px-4 py-3 hover:bg-accent/50 transition-colors duration-200">
            <div className="flex items-center gap-3">
              <div className={`h-10 w-10 rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-sm ${isSuperAdmin ? 'bg-gradient-to-br from-violet-400 to-violet-500 shadow-violet-400/20' : 'bg-gradient-to-br from-brand-indigo to-brand-royal shadow-brand-royal/20'}`}>
                {agent?.name?.charAt(0) || 'A'}
              </div>
              <div className="flex-1 overflow-hidden">
                <p className="text-sm font-heading font-semibold truncate leading-tight">{agent?.name}</p>
                {isSuperAdmin ? (
                  <span className="inline-flex items-center rounded-md bg-[#ede9fe] dark:bg-violet-900/40 px-2 py-0.5 text-[10px] font-semibold text-violet-600 dark:text-violet-300 mt-0.5">
                    Super Admin
                  </span>
                ) : (
                  <p className="text-xs text-muted-foreground capitalize leading-tight mt-0.5">{agent?.role}</p>
                )}
                {/* Plan: <badge> — show for super admins too (useful during
                    impersonation testing), and prefer the impersonated org's
                    tier over the logged-in agent's own tier when active. */}
                {displayedTier && (
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
                      {impersonatedOrg?.subscriptionTier ? 'Viewing as' : 'Plan'}
                    </span>
                    <TierBadge tier={displayedTier} size="sm" />
                  </div>
                )}
              </div>
            </div>
          </Link>
          )
        })()}
      </aside>
    </TooltipProvider>
  )
}
